import http from "../http-common";

class StudyService {
  // ─── Exam CRUD ───
  getExams() {
    return http.get("/study/exams");
  }
  getExamDetails(examId) {
    return http.get(`/study/exams/${examId}`);
  }
  createExam(data) {
    return http.post("/study/exams", data);
  }
  importStudyOutline(data) {
    return http.post("/study/import-outline", data);
  }
  updateExam(examId, data) {
    return http.put(`/study/exams/${examId}`, data);
  }
  deleteExam(examId) {
    return http.delete(`/study/exams/${examId}`);
  }

  // ─── Subject CRUD ───
  getSubjects(examId) {
    return http.get(`/study/exams/${examId}/subjects`);
  }
  createSubject(examId, data) {
    return http.post(`/study/exams/${examId}/subjects`, data);
  }
  updateSubject(subjectId, data) {
    return http.put(`/study/subjects/${subjectId}`, data);
  }
  deleteSubject(subjectId) {
    return http.delete(`/study/subjects/${subjectId}`);
  }

  // ─── Topic CRUD ───
  getTopics(subjectId) {
    return http.get(`/study/subjects/${subjectId}/topics`);
  }
  createTopic(subjectId, data) {
    return http.post(`/study/subjects/${subjectId}/topics`, data);
  }
  updateTopic(topicId, data) {
    return http.put(`/study/topics/${topicId}`, data);
  }
  deleteTopic(topicId) {
    return http.delete(`/study/topics/${topicId}`);
  }
  getSubTopics(topicId) {
    return http.get(`/study/topics/${topicId}/subtopics`);
  }
  extractSyllabusOutlineFromPdf(file, onProgress) {
    const formData = new FormData();
    formData.append("file", file);
    return http.post("/study/outline/extract-from-pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000,
      onUploadProgress: onProgress,
    });
  }
  structureSyllabusOutlineFromText(text, sourceName = "pasted-syllabus.txt") {
    return http.post("/study/outline/structure-text", { text, sourceName }, {
      timeout: 600000,
    });
  }

  // ─── Schedule ───
  getSchedule(date) {
    return http.get(`/study/schedule?date=${date}`);
  }
  getWeekSchedule(startDate) {
    return http.get(`/study/schedule/week?startDate=${startDate}`);
  }
  createSchedule(data) {
    return http.post("/study/schedule", data);
  }
  updateSchedule(scheduleId, data) {
    return http.put(`/study/schedule/${scheduleId}`, data);
  }
  toggleSchedule(scheduleId) {
    return http.put(`/study/schedule/${scheduleId}/toggle`);
  }
  deleteSchedule(scheduleId) {
    return http.delete(`/study/schedule/${scheduleId}`);
  }
  reorderSchedules(scheduleIds) {
    return http.put("/study/schedule/reorder", scheduleIds);
  }
  getOverdueSchedules() {
    return http.get("/study/schedule/overdue");
  }

  // ─── Calendar Sync ───
  getCalendarStatus() {
    return http.get("/study/calendar/status");
  }
  getGoogleCalendarAuthorizeUrl(state) {
    return http.post("/study/calendar/google/authorize", { state });
  }
  connectGoogleCalendar(code) {
    return http.post("/study/calendar/google/connect", { code });
  }
  disconnectGoogleCalendar() {
    return http.delete("/study/calendar/disconnect");
  }
  syncAllSchedulesToCalendar() {
    return http.post("/study/calendar/sync-all");
  }
  syncScheduleToCalendar(scheduleId) {
    return http.post(`/study/calendar/sync/${scheduleId}`);
  }

  // ─── Dashboard ───
  getDashboardStats() {
    return http.get("/study/dashboard");
  }
  getLeaderboard(period = "alltime") {
    return http.get(`/study/leaderboard?period=${period}`);
  }

  // ─── Session Logs ───
  createSessionLog(data) {
    return http.post("/study/session-log", data);
  }
  getSessionLogs() {
    return http.get("/study/session-logs");
  }

  // ─── Analytics ───
  getAnalytics() {
    return http.get("/study/analytics");
  }

  // ─── Smart Study Planner ───
  getSmartPlan() {
    return http.get("/study/smart-plan");
  }

  // ─── Spaced Revisions ───
  getUpcomingRevisions() {
    return http.get("/study/revisions/upcoming");
  }
  completeRevision(revisionId) {
    return http.put(`/study/revisions/${revisionId}/complete`);
  }

  // ─── Study XP ───
  getXPStats() {
    return http.get("/study/xp");
  }

  // ─── YouTube Notes ───
  getYouTubeNotes(videoId) {
    return http.get(`/study/youtube/notes?videoId=${videoId}`);
  }

  getYouTubePlaylist(url) {
    return http.get(`/study/youtube/playlist?url=${encodeURIComponent(url)}`);
  }

  uploadYouTubeCookies(cookies) {
    return http.post('/study/youtube/cookies', { cookies });
  }

  // ─── Tutorial Videos ───
  getTutorials() {
    return http.get("/study/tutorials");
  }
  addTutorial(data, asAdmin = false) {
    return http.post(`/study/tutorials?asAdmin=${asAdmin}`, data);
  }
  updateTutorial(id, data) {
    return http.put(`/study/tutorials/${id}`, data);
  }
  deleteTutorial(id) {
    return http.delete(`/study/tutorials/${id}`);
  }

  // ─── Pomodoro ───
  startPomodoro(data) {
    return http.post("/pomodoro/start", data);
  }
  completePomodoro(sessionId) {
    return http.put(`/pomodoro/${sessionId}/complete`);
  }
  getTodayPomodoros() {
    return http.get("/pomodoro/today");
  }
  getPomodoroStats() {
    return http.get("/pomodoro/stats");
  }

  // ─── Mock Test ───
  createQuestion(data) {
    return http.post("/mock-test/questions", data);
  }
  createBulkQuestions(data) {
    return http.post("/mock-test/questions/bulk", data);
  }
  getQuestionsByTopic(topicId) {
    return http.get(`/mock-test/questions/topic/${topicId}`);
  }
  getQuestionsBySubject(subjectId) {
    return http.get(`/mock-test/questions/subject/${subjectId}`);
  }
  updateQuestion(questionId, data) {
    return http.put(`/mock-test/questions/${questionId}`, data);
  }
  deleteQuestion(questionId) {
    return http.delete(`/mock-test/questions/${questionId}`);
  }
  generateMockTest(params) {
    return http.post("/mock-test/generate", params);
  }
  submitAttempt(data) {
    return http.post("/mock-test/submit", data);
  }
  getAttempts({ page = 0, size = 30 } = {}) {
    return http.get("/mock-test/attempts", { params: { page, size } });
  }
  getQuestionBank(examId, { page = 0, size = 40 } = {}) {
    return http.get("/mock-test/questions/bank", { params: { examId, page, size } });
  }
  getMockTestStats() {
    return http.get("/mock-test/stats");
  }
  extractQuestionsFromImage(file) {
    const formData = new FormData();
    formData.append("file", file);
    return http.post("/mock-test/extract-from-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });
  }

  // ─── Mock Test Papers (PDF → bilingual paper with sections) ───
  extractPaperFromPdf(file, onProgress, analyzer = null) {
    const formData = new FormData();
    formData.append("file", file);
    if (analyzer) {
      formData.append("analyzer", analyzer);
    }
    return http.post("/mock-test/extract-from-pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000,
      onUploadProgress: onProgress,
    });
  }
  extractPaperTextFromPdf(file, onProgress, options = {}) {
    const formData = new FormData();
    formData.append("file", file);
    const params = {
      includeImages: options.includeImages ?? true,
      maxImages: options.maxImages ?? 24,
      maxImageDimension: options.maxImageDimension ?? 1400,
    };
    return http.post("/mock-test/extract-text-from-pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000,
      onUploadProgress: onProgress,
      params,
    });
  }
  savePaper(paper) {
    return http.post("/mock-test/papers", paper);
  }
  listPapers({ page = 0, size = 24, kind, all = false, boardSlug, seriesSlug } = {}) {
    const params = { page, size, all };
    if (kind) params.kind = kind;
    if (boardSlug) params.boardSlug = boardSlug;
    if (seriesSlug) params.seriesSlug = seriesSlug;
    return http.get("/mock-test/papers", { params });
  }
  getPaperAttemptProgress() {
    return http.get("/mock-test/paper-attempt-progress");
  }
  getResultCardSummaries() {
    return http.get("/mock-test/paper-attempt-summaries");
  }
  getPaperAttempt(attemptId) {
    return http.get(`/mock-test/paper-attempts/${attemptId}`);
  }
  getPaper(paperId, sectionId) {
    return http.get(`/mock-test/papers/${paperId}`, {params: sectionId == null ? {} : {sectionId}});
  }
  getEditorPaper(paperId) {
    return http.get(`/mock-test/papers/${paperId}/editor`);
  }
  getAttemptReviewPaper(attemptId) {
    return http.get(`/mock-test/paper-attempts/${attemptId}/review-paper`);
  }
  updatePaper(paperId, data) {
    return http.put(`/mock-test/papers/${paperId}`, data);
  }
  adminUpdatePaper(paperId, data) {
    return http.put(`/mock-test/admin/papers/${paperId}`, data);
  }
  adminBulkRenameSections(paperId, findStr, replaceStr) {
    return http.put(`/mock-test/admin/papers/${paperId}/sections/rename`, { find: findStr, replace: replaceStr });
  }
  deletePaper(paperId) {
    return http.delete(`/mock-test/papers/${paperId}`);
  }
  deleteAllPapers() {
    return http.delete("/mock-test/papers");
  }
  getAdminPapers() {
    return http.get("/mock-test/admin/papers");
  }
  adminBulkUpdateCategory(paperIds, category) {
    return http.put("/mock-test/admin/papers/bulk-category", { paperIds, category });
  }
  adminDeletePaper(paperId) {
    return http.delete(`/mock-test/admin/papers/${paperId}`);
  }
  adminMergePapers(newName, paperIds) {
    return http.post("/mock-test/admin/papers/merge", { newName, paperIds });
  }
  uploadPaperToProd(paperData, prodUrl, prodToken) {
    const baseUrl = prodUrl.replace(/\/$/, "");
    return fetch(`${baseUrl}/api/mock-test/admin/papers/import-json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${prodToken}`
      },
      body: JSON.stringify(paperData)
    }).then(res => {
      if (!res.ok) {
        return res.json().then(data => { throw new Error(data.message || 'Upload failed'); });
      }
      return res.json();
    });
  }
  adminClearPaperResults(paperId) {
    return http.delete(`/mock-test/admin/papers/${paperId}/results`);
  }
  adminSyncPaperAnswers(paperId, authToken, examId = null) {
    return http.post(`/mock-test/admin/papers/${paperId}/sync-answers`, { authToken, examId });
  }
  getPaperTrialConfig(paperId) {
    return http.get(`/mock-test/admin/papers/${paperId}/trial-config`);
  }
  adminUpdatePaperTrialConfig(paperId, configData) {
    return http.put(`/mock-test/admin/papers/${paperId}/trial-config`, configData);
  }
  submitPaperAttempt(paperId, data) {
    return http.post(`/mock-test/papers/${paperId}/submit`, data);
  }
  getPaperAttempts(paperId) {
    return http.get(`/mock-test/papers/${paperId}/attempts`);
  }
  getAllPaperAttempts({ page = 0, size = 30, boardSlug, seriesSlug } = {}) {
    return http.get("/mock-test/paper-attempts", { params: { page, size, boardSlug, seriesSlug } });
  }
  importPapersFromTestbook(authToken, paperIds) {
    return http.post(
      "/mock-test/papers/import/testbook",
      { authToken, paperIds },
      { timeout: 300000 }
    );
  }
  syncTestbookAnswers(authToken, examId) {
    return http.post(
      "/mock-test/papers/import/testbook/sync-answers",
      { authToken, examId },
      { timeout: 120000 }
    );
  }


  // ─── Study Subscription ───
  getSubscriptionStatus() {
    return http.get("/study/subscription/status");
  }
  getPublicStudyPlan() {
    return http.get("/study/subscription/public-plan");
  }
  startStudyTrial() {
    return http.post("/study/subscription/start-trial");
  }
  createSubscriptionOrder() {
    return http.post("/study/subscription/create-order");
  }
  verifySubscription(data) {
    return http.post("/study/subscription/verify", data);
  }

  // ─── AI Mock Analysis ───
  generateAiAnalysis(attemptId) {
    return http.get(`/mock-ai/attempts/${attemptId}/analysis`);
  }
  generateCustomMock(data) {
    return http.post("/mock-ai/generate-custom", data);
  }
  chatWithAiTutor(data) {
    return http.post("/mock-ai/tutor/chat", data);
  }

  
  // ─── Analytics ───
  getRadarAnalytics() {
    return http.get("/mock-test/analytics/radar");
  }

  // ─── Bookmarks ───
  toggleMockBookmark(questionId) {
    return http.post(`/mock-test/bookmark/${questionId}`);
  }
  getMockBookmarks() {
    return http.get("/mock-test/bookmarks");
  }

  // ─── Exam Mock Hierarchy & Papers (Aspirant Zone) ───
  getExamHierarchyBoards(includeInactive = false) {
    return http.get("/mock-test/hierarchy/boards", { params: { includeInactive } });
  }

  getExamHierarchyPapers(seriesSlug, folderSlug) {
    const params = {};
    if (seriesSlug) params.seriesSlug = seriesSlug;
    if (folderSlug) params.folderSlug = folderSlug;
    return http.get("/mock-test/hierarchy/papers", { params });
  }

  getExamArchitecture(examCode) {
    return http.get(`/mock-test/hierarchy/architecture/${examCode}`);
  }

  adminSaveExamArchitecture(examCode, data) {
    return http.post(`/mock-test/admin/hierarchy/architecture/${examCode}`, data);
  }

  adminCreateExamBoard(data) {
    return http.post("/mock-test/admin/hierarchy/boards", data);
  }

  adminUpdateExamBoard(id, data) {
    return http.put(`/mock-test/admin/hierarchy/boards/${id}`, data);
  }

  adminDeleteExamBoard(id) {
    return http.delete(`/mock-test/admin/hierarchy/boards/${id}`);
  }

  adminCreateExamSeries(data) {
    return http.post("/mock-test/admin/hierarchy/series", data);
  }

  adminUpdateExamSeries(id, data) {
    return http.put(`/mock-test/admin/hierarchy/series/${id}`, data);
  }

  adminDeleteExamSeries(id) {
    return http.delete(`/mock-test/admin/hierarchy/series/${id}`);
  }

  adminCreateExamFolder(data) {
    return http.post("/mock-test/admin/hierarchy/folders", data);
  }

  adminUpdateExamFolder(id, data) {
    return http.put(`/mock-test/admin/hierarchy/folders/${id}`, data);
  }

  adminDeleteExamFolder(id) {
    return http.delete(`/mock-test/admin/hierarchy/folders/${id}`);
  }

  adminCreateExamPaper(paperData) {
    return http.post("/mock-test/admin/hierarchy/papers", paperData);
  }

  adminAddPaperQuestion(paperId, sectionId, questionData) {
    return http.post(`/mock-test/admin/hierarchy/papers/${paperId}/sections/${sectionId}/questions`, questionData);
  }

  adminBulkDeleteExamPapers(paperIds) {
    return http.post("/mock-test/admin/hierarchy/papers/bulk-delete", { paperIds });
  }

  adminBulkReassignExamPapers(paperIds, boardSlug, seriesSlug, folderSlug) {
    return http.put("/mock-test/admin/hierarchy/papers/bulk-reassign", { paperIds, boardSlug, seriesSlug, folderSlug });
  }

  // ─── MockMatrix Direct Importer ───
  syncMockMatrixHierarchy() {
    return http.post("/mock-test/admin/import/mockmatrix/sync-hierarchy");
  }

  importMockMatrixJson(payload) {
    return http.post("/mock-test/admin/import/mockmatrix/json", payload);
  }

  importMockMatrixFile(file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);
    if (options.boardSlug) formData.append("boardSlug", options.boardSlug);
    if (options.seriesSlug) formData.append("seriesSlug", options.seriesSlug);
    if (options.folderSlug) formData.append("folderSlug", options.folderSlug);
    if (options.overwrite) formData.append("overwrite", String(options.overwrite));
    return http.post("/mock-test/admin/import/mockmatrix/file", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  scanLocalMockMatrix(path = null) {
    return http.get("/mock-test/admin/import/mockmatrix/scan-local", { params: { path } });
  }

  batchImportLocalMockMatrix(body) {
    return http.post("/mock-test/admin/import/mockmatrix/batch-local", body);
  }

  // ─── E-Library Study Materials (Phase 3) ───
  getStudyMaterials(institutionId) {
    return http.get(`/study/student/materials?institutionId=${institutionId}`);
  }
}

const studyService = new StudyService();
export default studyService;
